import axios from 'axios';
import { getApiBaseUrl, refreshAccessToken } from './api';
import type {
  PartnerSessionDetail,
  PartnerSessionFile,
  PartnerSessionSummary,
  PersonSession,
  SessionAgentEvent,
  SessionAnswerValue,
  SessionFileAnswer,
  SessionKind,
  SessionPerson,
  SessionPurpose,
  SessionQuestion,
  SessionResponses,
  SessionTargets,
} from '../types/aai';

const base = () => `${getApiBaseUrl()}/aai`;
const scope = (clientId: string) => ({ params: { company_id: clientId } });

export interface DraftBody {
  title: string;
  description: string;
  kind: SessionKind;
  questions: SessionQuestion[];
  purpose: SessionPurpose;
  target_id: string;
  target_title: string;
}

/* The agent's reply arrives as server-sent events; axios cannot read a
   stream, so this one call uses fetch. It sends the same headers the axios
   interceptor would, and on a 401 refreshes the 15-minute access token once
   and retries, exactly as the interceptor does for every other call. */
const chatHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  let token: string | null = null;
  let impersonating = false;
  try {
    token = localStorage.getItem('auth_token');
    impersonating = localStorage.getItem('impersonation_token') !== null;
  } catch { /* storage refused */ }
  if (token) headers.Authorization = `Bearer ${token}`;
  if (token && impersonating) headers['X-Impersonation-Active'] = 'true';
  return headers;
};

const canRefresh = (): boolean => {
  try {
    return localStorage.getItem('refresh_token') !== null && localStorage.getItem('impersonation_token') === null;
  } catch {
    return false;
  }
};

const streamChat = async (
  clientId: string,
  sessionId: string,
  message: string,
  onEvent: (event: SessionAgentEvent) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const target = `${base()}/partner-sessions/${sessionId}/chat?company_id=${encodeURIComponent(clientId)}`;
  const post = () => fetch(target, { method: 'POST', headers: chatHeaders(), body: JSON.stringify({ message }), signal });
  let res = await post();
  if (res.status === 401 && canRefresh() && await refreshAccessToken()) res = await post();
  if (!res.ok || !res.body) throw new Error(`chat HTTP ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() || '';
    frames.forEach((frame) => {
      const line = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!line) return;
      try {
        onEvent(JSON.parse(line.slice(5).trim()) as SessionAgentEvent);
      } catch { /* a keepalive or partial frame */ }
    });
  }
};

export const partnerSessionsApi = {
  list: (clientId: string) =>
    axios.get<{ drafts: PartnerSessionSummary[]; sent: PartnerSessionSummary[] }>(`${base()}/partner-sessions`, scope(clientId)),
  create: (clientId: string) =>
    axios.post<PartnerSessionDetail>(`${base()}/partner-sessions`, null, scope(clientId)),
  get: (clientId: string, sessionId: string) =>
    axios.get<PartnerSessionDetail>(`${base()}/partner-sessions/${sessionId}`, scope(clientId)),
  update: (clientId: string, sessionId: string, body: DraftBody) =>
    axios.put<PartnerSessionDetail>(`${base()}/partner-sessions/${sessionId}`, body, scope(clientId)),
  remove: (clientId: string, sessionId: string) =>
    axios.delete<{ drafts: PartnerSessionSummary[]; sent: PartnerSessionSummary[] }>(`${base()}/partner-sessions/${sessionId}`, scope(clientId)),
  targets: (clientId: string) =>
    axios.get<SessionTargets>(`${base()}/partner-sessions/targets`, scope(clientId)),
  people: (clientId: string) =>
    axios.get<{ people: SessionPerson[] }>(`${base()}/partner-sessions/people`, scope(clientId)),
  upload: (clientId: string, sessionId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return axios.post<{ files: PartnerSessionFile[] }>(`${base()}/partner-sessions/${sessionId}/files`, form, scope(clientId));
  },
  removeFile: (clientId: string, sessionId: string, fileId: string) =>
    axios.delete<{ files: PartnerSessionFile[] }>(`${base()}/partner-sessions/${sessionId}/files/${fileId}`, scope(clientId)),
  chat: streamChat,
  send: (clientId: string, sessionId: string, emails: string[], remindEveryDays: number | null) =>
    axios.post<{ session: PartnerSessionDetail; added: string[] }>(`${base()}/partner-sessions/${sessionId}/send`, { emails, remind_every_days: remindEveryDays }, scope(clientId)),
  askAgain: (clientId: string, sessionId: string, email: string) =>
    axios.post<SessionResponses>(`${base()}/partner-sessions/${sessionId}/ask-again`, { email }, scope(clientId)),
  responses: (clientId: string, sessionId: string) =>
    axios.get<SessionResponses>(`${base()}/partner-sessions/${sessionId}/responses`, scope(clientId)),
  exportSession: (clientId: string, sessionId: string) =>
    axios.get<Blob>(`${base()}/partner-sessions/${sessionId}/export`, { ...scope(clientId), responseType: 'blob' }),
  personSessions: (clientId: string, email: string) =>
    axios.get<{ sessions: PersonSession[] }>(`${base()}/partner-responses/sessions`, { params: { company_id: clientId, email } }),
  exportResponses: (clientId: string, email = '') =>
    axios.get<Blob>(`${base()}/partner-responses/export`, { params: { company_id: clientId, email }, responseType: 'blob' }),
  uploadMine: (sessionId: string, questionId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return axios.post<{ files: SessionFileAnswer[] }>(`${base()}/me/partner-sessions/${sessionId}/questions/${encodeURIComponent(questionId)}/files`, form);
  },
  removeMine: (sessionId: string, questionId: string, fileId: string) =>
    axios.delete<{ files: SessionFileAnswer[] }>(`${base()}/me/partner-sessions/${sessionId}/questions/${encodeURIComponent(questionId)}/files/${fileId}`),
  answerFile: (clientId: string, sessionId: string, fileId: string) =>
    axios.get<Blob>(`${base()}/partner-sessions/${sessionId}/answer-files/${fileId}`, { ...scope(clientId), responseType: 'blob' }),
  submitMine: (sessionId: string, answers: Record<string, SessionAnswerValue>) =>
    axios.post<{ ok: boolean }>(`${base()}/me/partner-sessions/${sessionId}/submit`, { answers }),
};
